import React, { useState, useMemo, useCallback } from 'react';
import { AskRequest, AskRequestStatuses, AskRequestCommentVisibilities } from '@hone-automation/common';
import { prefixClass } from 'lib/utils';
import { Dropdown, DropdownItemConfig } from 'presentation/components/KSQ2/Common/Dropdown';
import { Tooltip } from 'presentation/components/KSQ2/Common/Tooltip';
import {
  IconActions,
  IconChevronDown,
  IconValidateNew,
  IconAssignmentNew,
  IconForward,
  IconReassign,
  IconFolder,
  IconFolderAdd,
  IconFolderMove,
  IconFolderDelete,
} from 'components/Icons';
import { usePatchQueueRequestMutation, useRecategorizeQueueRequestMutation } from 'hooks/queue/';
import { QueueView } from '@hone-automation/common';
import { useQueueContext } from 'presentation/components/Queue/QueueContext';
import { useConversationPanelContext } from '../../../../ConversationPanelContext';
import { useAuthContext } from 'context/useAuthContext';
import { useEvent } from 'hooks/useEvent';
import { useQueryState } from 'hooks/useQueryState';
import { RecategorizeDialog } from './RecategorizeDialog';
import { ResolveDialog } from './ResolveDialog';
import './ActionsDropdown.scss';
import classNames from 'classnames';
import { getRequestActionVisibility } from 'presentation/components/KSQ2/utils/actionVisibility';

/** Group item for dropdown */
interface GroupItem {
  id: string;
  name: string;
}

interface ActionsDropdownProps {
  request: AskRequest;
  noBorder?: boolean;
  /** Available groups for "Add to group" */
  groups?: GroupItem[];
  /** Current group ID (if request is in a group) - enables "Remove from group" */
  currentGroupId?: string | null;
  /** Handler for adding request to a group */
  onAddToGroup?: (groupId: string) => void;
  /** Handler for removing request from current group */
  onRemoveFromGroup?: () => void;
  /** Whether this request IS a group (type='Group') - shows dissolve action only */
  isGroup?: boolean;
  /** Handler for dissolving a group (removes all children) */
  onDissolveGroup?: () => void;
  /** Handler for creating a new group */
  onCreateGroup?: () => void;
}

/**
 * Actions dropdown with lightning bolt trigger
 * Contains: Send to Client, Re-categorize, Resolve
 *
 * Works in two modes:
 * - Queue mode: Uses QueueContext for state management
 * - DataRowModal mode: Uses ConversationPanelContext callbacks
 */
export const ActionsDropdown: React.FC<ActionsDropdownProps> = ({
  request,
  noBorder,
  groups = [],
  currentGroupId,
  onAddToGroup,
  onRemoveFromGroup,
  isGroup = false,
  onDissolveGroup,
  onCreateGroup,
}) => {
  const prefix = prefixClass('conversation-header-actions');
  const { isBookkeeperAdmin, userId } = useAuthContext();
  const { dispatch, state } = useQueueContext();
  const { requests, filters, activeView } = state;
  const [, setRequestId] = useQueryState('requestId');
  const [status] = useQueryState('status');
  const { dispatch: emitResolveEvent } = useEvent('onQueueRequestResolve');
  const { dispatch: emitStatusChangeEvent } = useEvent('onQueueRequestChangeStatus');

  // Get ConversationPanelContext for standalone mode (DataRowModal)
  const {
    mode,
    onRequestUpdate,
    refetchFlaggedTransactions,
    locationId: contextLocationId,
  } = useConversationPanelContext();
  const isStandaloneMode = mode === 'dataRowModal';
  const effectiveLocationId = contextLocationId || request.locationId;

  /**
   * Check if a request belongs to the current activeView
   */
  const requestBelongsToView = useCallback(
    (req: AskRequest): boolean => {
      if (!activeView) return req.status === status; // Fallback to legacy behavior
      // Blockers are shown in Blockers view
      if (activeView === 'Blockers') return req.priority === 'Blocker';
      if (req.priority === 'Blocker') return false; // Blockers excluded from other views

      switch (activeView) {
        case 'Me':
          return req.status === 'Open' && req.assignee === userId;
        case 'My Team':
          return req.status === 'Open' && req.assignee !== userId;
        case 'Client':
          return req.status === 'In Progress';
        case 'Completed':
          return req.status === 'Closed';
        default:
          return false;
      }
    },
    [activeView, status, userId]
  );

  /**
   * Get the next request in the current view after excluding one
   */
  const getNextRequestInView = useCallback(
    (excludeId: string): AskRequest | null => {
      const remainingRequests = requests
        .filter(req => req.id !== excludeId && requestBelongsToView(req))
        .sort((a, b) => {
          return new Date(a.updatedAt || a.createdAt).getTime() - new Date(b.updatedAt || b.createdAt).getTime();
        });
      return remainingRequests[0] || null;
    },
    [requests, requestBelongsToView]
  );

  const [isRecategorizeOpen, setIsRecategorizeOpen] = useState(false);
  const [isResolveOpen, setIsResolveOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  /**
   * Highlight a filter with drop animation, then auto-clear
   * Maps status to QueueView and dispatches HIGHLIGHT_FILTER
   */
  const highlightFilter = useCallback(
    (targetStatus: string) => {
      let targetFilter: QueueView | null = null;

      switch (targetStatus) {
        case 'Open':
          targetFilter = 'Me'; // Default to 'Me' for Open status
          break;
        case 'In Progress':
          targetFilter = 'Client';
          break;
        case 'Closed':
          targetFilter = 'Completed';
          break;
      }

      if (targetFilter) {
        dispatch({ type: 'HIGHLIGHT_FILTER', payload: targetFilter });
        // Clear highlight after animation completes (500ms)
        setTimeout(() => {
          dispatch({ type: 'HIGHLIGHT_FILTER', payload: null });
        }, 500);
      }
    },
    [dispatch]
  );

  // Manual Request mutation (toggle between Open and In Progress)
  const manualRequestMutation = usePatchQueueRequestMutation(trx => {
    if (!trx) return;

    // Standalone mode: use callbacks instead of QueueContext
    if (isStandaloneMode) {
      onRequestUpdate?.(trx as unknown as AskRequest);
      refetchFlaggedTransactions?.();
      return;
    }

    // Queue mode: Update the request in the list via QueueContext
    dispatch({
      type: 'UPDATE_REQUEST',
      payload: {
        request: trx as unknown as AskRequest,
        status: trx.status,
      },
    });

    // Highlight target filter with drop animation
    highlightFilter(trx.status);

    // Emit event to trigger animation on target tab
    const targetStatus = trx.status === 'Open' ? 'For Me' : 'For Client';
    emitStatusChangeEvent({
      field: trx.status,
      duration: 3000,
      message: `Request moved to ${targetStatus}.`,
    });

    // Select next request in the current view
    const nextRequest = getNextRequestInView(trx.id);
    dispatch({ type: 'SET_SELECTED_ITEM', payload: nextRequest });
    setRequestId(nextRequest?.id || undefined);
  });

  // Recategorize mutation
  const recategorizeMutation = useRecategorizeQueueRequestMutation(
    effectiveLocationId || '',
    request.status,
    (_, trx) => {
      const statusChanged = trx.status !== request.status;

      // Standalone mode: use callbacks instead of QueueContext
      if (isStandaloneMode) {
        onRequestUpdate?.(trx);
        refetchFlaggedTransactions?.();
        setIsRecategorizeOpen(false);
        return;
      }

      // Queue mode: Update via QueueContext
      dispatch({
        type: 'UPDATE_REQUEST',
        payload: {
          request: trx,
          status: trx.status,
        },
      });

      // Highlight target filter with drop animation if status changed
      if (statusChanged) {
        highlightFilter(trx.status);
        // Select next request since item moved
        const nextRequest = getNextRequestInView(trx.id);
        dispatch({ type: 'SET_SELECTED_ITEM', payload: nextRequest });
        setRequestId(nextRequest?.id || undefined);
      } else {
        // Stay on updated item
        dispatch({ type: 'SET_SELECTED_ITEM', payload: trx });
      }

      emitStatusChangeEvent({
        field: trx.status,
        duration: 5000,
        message: statusChanged
          ? `Request status updated to ${trx.status}. Go to ${trx.status} tab to view it.`
          : undefined,
      });

      setIsRecategorizeOpen(false);
    }
  );

  // Resolve mutation
  const resolveMutation = usePatchQueueRequestMutation((trx: any | undefined) => {
    if (!trx) return;

    const updatedTrx = {
      ...trx,
      status: 'Closed',
    } as unknown as AskRequest;

    // Standalone mode: use callbacks instead of QueueContext
    if (isStandaloneMode) {
      onRequestUpdate?.(updatedTrx);
      refetchFlaggedTransactions?.();
      setIsResolveOpen(false);
      return;
    }

    // Queue mode: Update via QueueContext
    dispatch({
      type: 'UPDATE_REQUEST',
      payload: {
        request: updatedTrx,
        status: 'Closed',
      },
    });

    // Highlight the Closed filter with drop animation
    highlightFilter('Closed');

    emitResolveEvent({
      field: filters.status,
      duration: 3000,
      message: 'Request marked as solved.',
    });

    // Select next request in the current view
    const nextRequest = getNextRequestInView(trx.id);
    dispatch({ type: 'SET_SELECTED_ITEM', payload: nextRequest });
    setRequestId(nextRequest?.id || undefined);

    setIsResolveOpen(false);
  });

  const handleManualRequest = () => {
    const newStatus =
      request.status === AskRequestStatuses.In_Progress ? AskRequestStatuses.Open : AskRequestStatuses.In_Progress;
    if (isStandaloneMode) {
      onRequestUpdate?.({ ...request, status: newStatus } as AskRequest);
      refetchFlaggedTransactions?.();
    }
    manualRequestMutation.mutate({ id: request.id, status: newStatus, locationId: request.locationId });
  };

  const handleRecategorize = () => {
    setIsRecategorizeOpen(true);
  };

  const handleMarkAsSolved = () => {
    setIsResolveOpen(true);
  };

  const handleRecategorizeSubmit = ({ comment, account }: { comment: string; account: string }) => {
    recategorizeMutation.mutate({ trxId: request.id, newAccountId: account, comment });
  };

  const handleResolveSubmit = ({ comment }: { comment: string }) => {
    if (isStandaloneMode) {
      onRequestUpdate?.({ ...request, status: 'Closed' } as AskRequest);
      refetchFlaggedTransactions?.();
    }
    resolveMutation.mutate({ txId: request.id, status: 'Closed', comment, locationId: request.locationId });
  };

  // Determine manual request label
  const manualRequestLabel = useMemo(() => {
    if (request.status === AskRequestStatuses.In_Progress) {
      return 'Move to "For Me"';
    }
    return 'Move to "For Client"';
  }, [request.status]);

  // Determine which actions are available
  const isInternal =
    request.comments &&
    request.comments.length > 0 &&
    request.comments.every(comment => comment.visibility === AskRequestCommentVisibilities.Internal);
  const { isClosed, showResolve, showRecategorize } = getRequestActionVisibility(request, { isBookkeeperAdmin });

  // Build dropdown items based on available actions
  const items: DropdownItemConfig[] = [];

  // For groups, only show Dissolve action
  if (isGroup) {
    if (onDissolveGroup) {
      items.push({
        id: 'dissolve-group',
        label: 'Dissolve Group',
        icon: <IconFolderDelete />,
        onClick: () => {
          onDissolveGroup();
          setDropdownOpen(false);
        },
      });
    }
    // Skip all other actions for groups (guarded by !isGroup below)
  }

  // Manual Request - show if not internal and not closed (skip for groups)
  if (!isGroup && !isInternal && !isClosed) {
    items.push({
      id: 'manual-request',
      label: manualRequestLabel,
      icon: request.status === AskRequestStatuses.In_Progress ? <IconForward /> : <IconAssignmentNew />,
      onClick: () => {
        handleManualRequest();
        setDropdownOpen(false);
      },
      disabled: manualRequestMutation.isPending,
    });
  }

  // Re-categorize - show if user has permission and request allows it (skip for groups)
  if (!isGroup && showRecategorize) {
    items.push({
      id: 'recategorize',
      label: 'Re-Categorize',
      icon: <IconReassign />,
      onClick: () => {
        handleRecategorize();
        setDropdownOpen(false);
      },
    });
  }

  // Resolve - show if not closed (skip for groups)
  if (!isGroup && showResolve) {
    items.push({
      id: 'mark-solved',
      label: 'Resolve',
      icon: <IconValidateNew />,
      onClick: () => {
        handleMarkAsSolved();
        setDropdownOpen(false);
      },
    });
  }

  // Group actions - either/or based on whether item is in a group (skip for groups themselves)
  const isInGroup = !!currentGroupId;

  // Create group - show if not in a group and not a group itself
  if (!isGroup && !isInGroup && onCreateGroup) {
    items.push({
      id: 'create-group',
      label: 'Create group',
      icon: <IconFolder />,
      onClick: () => {
        onCreateGroup();
        setDropdownOpen(false);
      },
    });
  }

  // Add to group - show if not in a group and not a group itself
  if (!isGroup && !isInGroup && onAddToGroup) {
    // Build submenu items for groups
    const groupSubmenuItems =
      groups.length > 0
        ? groups.map(group => ({
            id: `add-to-group-${group.id}`,
            label: group.name,
            onClick: () => {
              onAddToGroup(group.id);
              setDropdownOpen(false);
            },
          }))
        : [
            {
              id: 'no-groups',
              label: 'No groups available',
              onClick: () => {},
              disabled: true,
            },
          ];

    items.push({
      id: 'add-to-group',
      label: 'Add to group',
      icon: <IconFolderAdd />,
      onClick: () => {},
      submenu: groupSubmenuItems,
    });
  }

  // Remove from group - show if in a group and not a group itself (before Move to another group)
  if (!isGroup && isInGroup && onRemoveFromGroup) {
    items.push({
      id: 'remove-from-group',
      label: 'Remove from group',
      icon: <IconFolderDelete />,
      onClick: () => {
        onRemoveFromGroup();
        setDropdownOpen(false);
      },
    });
  }

  // Move to another group - show if in a group and not a group itself
  if (!isGroup && isInGroup && onAddToGroup) {
    // Build submenu items for groups (excluding current group)
    const moveGroupSubmenuItems =
      groups.length > 0
        ? groups
            .filter(group => group.id !== currentGroupId) // Exclude current group
            .map(group => ({
              id: `move-to-group-${group.id}`,
              label: group.name,
              onClick: () => {
                onAddToGroup(group.id);
                setDropdownOpen(false);
              },
            }))
        : [];

    // Only show if there are other groups available
    if (moveGroupSubmenuItems.length > 0) {
      items.push({
        id: 'move-to-group',
        label: 'Move to another group',
        icon: <IconFolderMove />,
        onClick: () => {},
        submenu: moveGroupSubmenuItems,
      });
    }
  }

  // Don't render if no actions available
  if (items.length === 0) {
    return null;
  }

  // Check if we should use simple button vs dropdown
  // Use dropdown if multiple items OR if single item has a submenu
  const hasSingleItemWithSubmenu = items.length === 1 && items[0].submenu && items[0].submenu.length > 0;
  const useSimpleButton = items.length === 1 && !hasSingleItemWithSubmenu;

  return (
    <div className={prefix()}>
      {useSimpleButton ? (
        <Tooltip content={items[0].label} position="top" alignment="center">
          <div
            className={classNames(prefix('trigger-wrapper'), { disabled: items[0].disabled })}
            role="button"
            aria-disabled={items[0].disabled}
            onClick={() => {
              if (!items[0].disabled) {
                items[0].onClick?.();
              }
            }}
          >
            {items[0].icon}
            <span>{items[0].label}</span>
          </div>
        </Tooltip>
      ) : (
        <Dropdown
          trigger={
            <Tooltip content="Actions" position="top" alignment="center">
              <div className={prefix('trigger-wrapper')}>
                <IconActions />
                {noBorder && 'Actions'}
                <div className={prefix('chevron')}>
                  <IconChevronDown />
                </div>
              </div>
            </Tooltip>
          }
          items={items}
          position="bottom"
          alignment="start"
          offset={{ x: 30, y: 0 }}
          menuClassName={prefix('menu')}
          tooltip="Available Actions"
          isOpen={dropdownOpen}
          onOpenChange={setDropdownOpen}
          closeOnItemClick={false}
        />
      )}

      {/* Recategorize Dialog */}
      <RecategorizeDialog
        isOpen={isRecategorizeOpen}
        isPending={recategorizeMutation.isPending}
        onClose={() => setIsRecategorizeOpen(false)}
        onSubmit={handleRecategorizeSubmit}
      />

      {/* Resolve Dialog */}
      <ResolveDialog
        isOpen={isResolveOpen}
        isPending={resolveMutation.isPending}
        onClose={() => setIsResolveOpen(false)}
        onSubmit={handleResolveSubmit}
      />
    </div>
  );
};

export default ActionsDropdown;
